import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  setDoc,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from '../config/firebase';
import {
  UserNotification,
  NotificationType,
  NotificationPriority,
  NotificationPreferences,
  CreateNotificationParams,
} from '../types/user';
import { SystemConfigService } from './systemConfigService';

/**
 * NotificationService - Manages user notifications across multiple channels
 *
 * Features:
 * - Multi-channel architecture (in-app, email, push, SMS)
 * - User-controlled preferences per channel and category
 * - Priority-based delivery
 * - Notification grouping and batching
 * - Auto-cleanup and archiving
 *
 * Phase 1: In-app notifications only
 * Phase 4: Email, push, and SMS delivery
 */
class NotificationServiceImpl {
  private readonly COLLECTION_NAME = 'notifications';
  private readonly PREFERENCES_COLLECTION = 'notificationPreferences';

  // ==================== CREATION ====================

  /**
   * Create a notification for a user
   *
   * @param params Notification parameters
   * @returns Created notification or mock if notifications disabled
   */
  async createNotification(
    params: CreateNotificationParams
  ): Promise<UserNotification> {
    try {
      // STEP 1: Check if notifications are enabled in system config
      const config = await SystemConfigService.getConfig();

      if (!config.email.enableNotifications) {
        console.log('Notifications disabled in system config');
        return this.createMockNotification(params);
      }

      // STEP 2: Check user preferences
      const prefs = await this.getPreferences(params.userId);
      if (!prefs.enabled) {
        console.log(`Notifications disabled for user ${params.userId}`);
        return this.createMockNotification(params);
      }

      // STEP 3: Check if user has paused notifications
      if (prefs.pausedUntil && prefs.pausedUntil > new Date()) {
        console.log(`Notifications paused for user ${params.userId} until ${prefs.pausedUntil}`);
        return this.createMockNotification(params);
      }

      // STEP 4: Check category preference
      const category = this.getCategoryForType(params.type);
      if (!prefs.categories[category]) {
        console.log(`Category ${category} disabled for user ${params.userId}`);
        return this.createMockNotification(params);
      }

      // STEP 5: Check if in-app channel is enabled
      if (!prefs.channels.inApp.enabled) {
        console.log(`In-app notifications disabled for user ${params.userId}`);
        return this.createMockNotification(params);
      }

      // STEP 6: Build notification document
      const priority = params.priority || this.getDefaultPriority(params.type);
      const title = params.title || this.getDefaultTitle(params.type);
      const message = params.message || this.getDefaultMessage(params.type, params.data);

      // Build base notification data (exclude undefined fields - Firestore doesn't accept them)
      const notificationData: any = {
        userId: params.userId,
        type: params.type,
        title,
        message,
        priority,
        dismissible: params.dismissible !== false,
        data: this.removeUndefinedFields(params.data || {}),
        read: false,
        archived: false,
        createdBy: 'system',
        source: 'automated' as const,
        createdAt: serverTimestamp(),
      };

      // Only add optional fields if they have values
      if (params.actionUrl) {
        notificationData.actionUrl = params.actionUrl;
      }
      if (params.actionLabel) {
        notificationData.actionLabel = params.actionLabel;
      }
      if (params.groupKey) {
        notificationData.groupKey = params.groupKey;
      }

      // STEP 7: Create in Firestore
      const docRef = await addDoc(
        collection(db, this.COLLECTION_NAME),
        notificationData
      );

      // STEP 8: Return created notification with ID
      return {
        id: docRef.id,
        ...notificationData,
        createdAt: new Date(),
      } as UserNotification;
    } catch (error: any) {
      // Handle Firebase permission errors specifically
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        console.warn('Permission denied creating notification - user may not have write access:', params.userId);
      } else {
        console.error('Error creating notification:', error);
      }
      // Don't throw - notifications are non-critical
      return this.createMockNotification(params);
    }
  }

  /**
   * Create notifications for multiple users (bulk)
   *
   * @param userIds Array of user IDs
   * @param params Notification parameters (without userId)
   */
  async createBulkNotification(
    userIds: string[],
    params: Omit<CreateNotificationParams, 'userId'>
  ): Promise<void> {
    try {
      // Create notifications in parallel (but respect user preferences)
      await Promise.all(
        userIds.map(userId =>
          this.createNotification({ ...params, userId })
        )
      );
    } catch (error: any) {
      console.error('Error creating bulk notifications:', error);
      // Don't throw - notifications are non-critical
    }
  }

  // ==================== RETRIEVAL ====================

  /**
   * Get notifications for a user with optional filters
   *
   * @param userId User ID
   * @param options Query options
   * @returns Array of notifications
   */
  async getUserNotifications(
    userId: string,
    options?: {
      limit?: number;
      unreadOnly?: boolean;
      types?: NotificationType[];
      includeArchived?: boolean;
    }
  ): Promise<UserNotification[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('userId', '==', userId),
      ];

      // Filter by read status
      if (options?.unreadOnly) {
        constraints.push(where('read', '==', false));
      }

      // Filter by archived status
      if (!options?.includeArchived) {
        constraints.push(where('archived', '==', false));
      }

      // Filter by types (Firestore 'in' operator supports max 10 values)
      if (options?.types && options.types.length > 0 && options.types.length <= 10) {
        constraints.push(where('type', 'in', options.types));
      }

      // Order by creation date (newest first)
      constraints.push(orderBy('createdAt', 'desc'));

      // Limit results
      constraints.push(firestoreLimit(options?.limit || 50));

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => this.mapDocumentToNotification(doc));
    } catch (error: any) {
      console.error('Error getting notifications:', error);
      return [];
    }
  }

  /**
   * Get a single notification by ID
   *
   * @param notificationId Notification ID
   * @returns Notification or null
   */
  async getById(notificationId: string): Promise<UserNotification | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, notificationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.mapDocumentToNotification(docSnap);
    } catch (error: any) {
      console.error('Error getting notification by ID:', error);
      return null;
    }
  }

  /**
   * Get unread notification count
   *
   * @param userId User ID
   * @returns Count of unread notifications
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const unreadNotifications = await this.getUserNotifications(userId, {
        unreadOnly: true,
        limit: 100,
      });
      return unreadNotifications.length;
    } catch (error: any) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // ==================== UPDATES ====================

  /**
   * Mark notification as read
   *
   * @param notificationId Notification ID
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, notificationId);
      await updateDoc(docRef, {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Mark multiple notifications as read
   *
   * @param notificationIds Array of notification IDs
   */
  async markManyAsRead(notificationIds: string[]): Promise<void> {
    try {
      await Promise.all(
        notificationIds.map(id => this.markAsRead(id))
      );
    } catch (error: any) {
      console.error('Error marking multiple notifications as read:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Mark all user notifications as read
   *
   * @param userId User ID
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const unreadNotifications = await this.getUserNotifications(userId, {
        unreadOnly: true,
        limit: 100,
      });

      await this.markManyAsRead(
        unreadNotifications.map(n => n.id)
      );
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Mark a notification as unread
   *
   * @param notificationId Notification ID
   */
  async markAsUnread(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, notificationId);
      await updateDoc(docRef, {
        read: false,
        readAt: null,
      });
    } catch (error: any) {
      console.error('Error marking notification as unread:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Archive a notification
   *
   * @param notificationId Notification ID
   */
  async archive(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, notificationId);
      await updateDoc(docRef, {
        archived: true,
        archivedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error archiving notification:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Unarchive a notification
   *
   * @param notificationId Notification ID
   */
  async unarchive(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, notificationId);
      await updateDoc(docRef, {
        archived: false,
        archivedAt: null,
      });
    } catch (error: any) {
      console.error('Error unarchiving notification:', error);
      // Don't throw - non-critical
    }
  }

  /**
   * Delete a notification (actually archives it)
   *
   * @param notificationId Notification ID
   */
  async delete(notificationId: string): Promise<void> {
    try {
      // We use soft delete (archive) instead of hard delete
      await this.archive(notificationId);
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      // Don't throw - non-critical
    }
  }

  // ==================== PREFERENCES ====================

  /**
   * Get user notification preferences
   *
   * @param userId User ID
   * @returns User preferences or defaults
   */
  async getPreferences(userId: string): Promise<NotificationPreferences> {
    try {
      const docRef = doc(db, this.PREFERENCES_COLLECTION, userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Return default preferences
        return this.getDefaultPreferences(userId);
      }

      const data = docSnap.data();
      return {
        userId,
        enabled: data.enabled ?? true,
        pausedUntil: data.pausedUntil?.toDate(),
        channels: data.channels || this.getDefaultPreferences(userId).channels,
        categories: data.categories || this.getDefaultPreferences(userId).categories,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        version: data.version || 1,
      };
    } catch (error: any) {
      console.error('Error getting notification preferences:', error);
      return this.getDefaultPreferences(userId);
    }
  }

  /**
   * Update user notification preferences
   *
   * @param userId User ID
   * @param updates Partial preference updates
   */
  async updatePreferences(
    userId: string,
    updates: Partial<NotificationPreferences>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.PREFERENCES_COLLECTION, userId);

      // Check if document exists
      const docSnap = await getDoc(docRef);

      const updateData: any = {
        ...updates,
        userId,
        updatedAt: serverTimestamp(),
      };

      // Remove undefined fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      if (!docSnap.exists()) {
        // Create new preferences document
        await setDoc(docRef, {
          ...this.getDefaultPreferences(userId),
          ...updateData,
        });
      } else {
        // Update existing
        await updateDoc(docRef, updateData);
      }
    } catch (error: any) {
      console.error('Error updating notification preferences:', error);
      throw new Error(`Failed to update notification preferences: ${error.message}`);
    }
  }

  /**
   * Enable/disable all notifications for a user
   *
   * @param userId User ID
   * @param enabled Enable or disable
   */
  async setEnabled(userId: string, enabled: boolean): Promise<void> {
    await this.updatePreferences(userId, { enabled });
  }

  /**
   * Pause notifications for a duration (snooze)
   *
   * @param userId User ID
   * @param durationHours How many hours to pause
   */
  async pauseNotifications(userId: string, durationHours: number): Promise<void> {
    const pausedUntil = new Date();
    pausedUntil.setHours(pausedUntil.getHours() + durationHours);

    await this.updatePreferences(userId, { pausedUntil });
  }

  // ==================== HELPERS ====================

  /**
   * Map Firestore document to UserNotification
   */
  private mapDocumentToNotification(doc: any): UserNotification {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      type: data.type,
      title: data.title,
      message: data.message,
      priority: data.priority || NotificationPriority.NORMAL,
      actionUrl: data.actionUrl,
      actionLabel: data.actionLabel,
      dismissible: data.dismissible ?? true,
      data: data.data || {},
      groupKey: data.groupKey,
      read: data.read ?? false,
      readAt: data.readAt?.toDate(),
      archived: data.archived ?? false,
      archivedAt: data.archivedAt?.toDate(),
      createdBy: data.createdBy || 'system',
      source: data.source || 'automated',
      createdAt: data.createdAt?.toDate() || new Date(),
    };
  }

  /**
   * Get default preferences for a new user
   */
  private getDefaultPreferences(userId: string): NotificationPreferences {
    return {
      userId,
      enabled: true,
      channels: {
        inApp: {
          enabled: true,
        },
        email: {
          enabled: true,
          digestMode: 'daily',
          digestTime: '09:00',
        },
      },
      categories: {
        authentication: true,
        contentModeration: true,
        socialInteractions: true,
        familyTreeUpdates: true,
        researchCollaboration: true,
        reminders: true,
        systemAnnouncements: true,
      },
      updatedAt: new Date(),
      version: 1,
    };
  }

  /**
   * Get category for notification type
   */
  private getCategoryForType(type: NotificationType): keyof NotificationPreferences['categories'] {
    const categoryMap: Record<NotificationType, keyof NotificationPreferences['categories']> = {
      // Authentication & User Management
      welcome: 'authentication',
      email_verified: 'authentication',
      approval_granted: 'authentication',
      approval_denied: 'authentication',
      role_changed: 'authentication',
      account_suspended: 'authentication',
      account_reactivated: 'authentication',
      password_reset: 'authentication',
      person_link_requested: 'authentication',
      person_link_approved: 'authentication',
      person_link_rejected: 'authentication',
      person_link_removed: 'authentication',

      // Content Moderation
      content_submitted: 'contentModeration',
      content_approved: 'contentModeration',
      content_rejected: 'contentModeration',
      content_flagged: 'contentModeration',
      change_approved: 'contentModeration',
      change_rejected: 'contentModeration',
      edit_approved: 'contentModeration',
      edit_rejected: 'contentModeration',

      // Social Interactions
      comment_mention: 'socialInteractions',
      comment_reply: 'socialInteractions',
      comment_on_content: 'socialInteractions',
      new_comment: 'socialInteractions',
      photo_tagged: 'socialInteractions',
      relationship_suggested: 'socialInteractions',

      // Family Tree Updates
      person_added: 'familyTreeUpdates',
      person_updated: 'familyTreeUpdates',
      relationship_added: 'familyTreeUpdates',
      document_uploaded: 'familyTreeUpdates',
      photo_uploaded: 'familyTreeUpdates',
      memory_shared: 'familyTreeUpdates',
      memory_added: 'familyTreeUpdates',
      document_shared: 'familyTreeUpdates',

      // Research Collaboration
      research_note_assigned: 'researchCollaboration',
      research_note_updated: 'researchCollaboration',
      research_note_completed: 'researchCollaboration',
      research_request: 'researchCollaboration',
      research_response: 'researchCollaboration',
      collaboration_invite: 'researchCollaboration',

      // Reminders & Events
      birthday_reminder: 'reminders',
      anniversary_reminder: 'reminders',
      death_anniversary: 'reminders',
      task_reminder: 'reminders',
      profile_incomplete: 'reminders',

      // System & Admin
      system_announcement: 'systemAnnouncements',
      system_update: 'systemAnnouncements',
      maintenance_scheduled: 'systemAnnouncements',
      feature_announcement: 'systemAnnouncements',
      privacy_policy_update: 'systemAnnouncements',
      export_ready: 'systemAnnouncements',
      import_completed: 'systemAnnouncements',
      import_failed: 'systemAnnouncements',
      admin_broadcast: 'systemAnnouncements',
      custom: 'systemAnnouncements',

      // Messaging (mapped to socialInteractions)
      new_message: 'socialInteractions',
      message_mention: 'socialInteractions',
      message_reply: 'socialInteractions',
      message_reaction: 'socialInteractions',
      group_chat_added: 'socialInteractions',
      group_chat_removed: 'socialInteractions',
      group_chat_name_changed: 'socialInteractions',
      group_chat_member_joined: 'socialInteractions',
      group_chat_member_left: 'socialInteractions',

      // Mentions (additional types)
      note_mention: 'socialInteractions',
      memory_mention: 'socialInteractions',
      feature_request_mention: 'socialInteractions',
    };

    return categoryMap[type] || 'systemAnnouncements';
  }

  /**
   * Get default title for notification type
   */
  private getDefaultTitle(type: NotificationType): string {
    const titleMap: Record<NotificationType, string> = {
      // Authentication & User Management
      welcome: 'Welcome to Martz Genealogy!',
      email_verified: 'Email Verified',
      approval_granted: 'Account Approved',
      approval_denied: 'Account Application',
      role_changed: 'Role Updated',
      account_suspended: 'Account Suspended',
      account_reactivated: 'Account Reactivated',
      password_reset: 'Password Reset',
      person_link_requested: 'Profile Link Request',
      person_link_approved: 'Profile Link Approved',
      person_link_rejected: 'Profile Link Denied',
      person_link_removed: 'Profile Link Removed',

      // Content Moderation
      content_submitted: 'Content Submitted',
      content_approved: 'Content Approved',
      content_rejected: 'Content Rejected',
      content_flagged: 'Content Flagged',
      change_approved: 'Change Approved',
      change_rejected: 'Change Rejected',
      edit_approved: 'Edit Approved',
      edit_rejected: 'Edit Rejected',

      // Social Interactions
      comment_mention: 'You were mentioned',
      comment_reply: 'New reply to your comment',
      comment_on_content: 'New comment on your content',
      new_comment: 'New comment',
      photo_tagged: 'You were tagged in a photo',
      relationship_suggested: 'Relationship suggested',

      // Family Tree Updates
      person_added: 'New person added',
      person_updated: 'Person profile updated',
      relationship_added: 'New relationship added',
      document_uploaded: 'New document uploaded',
      photo_uploaded: 'New photo uploaded',
      memory_shared: 'New memory shared',
      memory_added: 'New memory added',
      document_shared: 'Document shared',

      // Research Collaboration
      research_note_assigned: 'Research task assigned',
      research_note_updated: 'Research note updated',
      research_note_completed: 'Research task completed',
      research_request: 'Research request',
      research_response: 'Research response',
      collaboration_invite: 'Collaboration invitation',

      // Reminders & Events
      birthday_reminder: 'Birthday reminder',
      anniversary_reminder: 'Anniversary reminder',
      death_anniversary: 'Death anniversary',
      task_reminder: 'Task reminder',
      profile_incomplete: 'Complete your profile',

      // System & Admin
      system_announcement: 'System Announcement',
      system_update: 'System Update',
      maintenance_scheduled: 'Scheduled Maintenance',
      feature_announcement: 'New Feature',
      privacy_policy_update: 'Privacy Policy Update',
      export_ready: 'Export Ready',
      import_completed: 'Import Completed',
      import_failed: 'Import Failed',
      admin_broadcast: 'Admin Broadcast',
      custom: 'Notification',

      // Messaging
      new_message: 'New Message',
      message_mention: 'You were mentioned',
      message_reply: 'Reply to your message',
      message_reaction: 'Reaction to your message',
      group_chat_added: 'Added to group chat',
      group_chat_removed: 'Removed from group chat',
      group_chat_name_changed: 'Group name changed',
      group_chat_member_joined: 'Member joined group',
      group_chat_member_left: 'Member left group',

      // Mentions (additional types)
      note_mention: 'You were mentioned in a note',
      memory_mention: 'You were mentioned in a memory',
      feature_request_mention: 'You were mentioned in a feature request',
    };

    return titleMap[type] || 'Notification';
  }

  /**
   * Get default message for notification type
   */
  private getDefaultMessage(type: NotificationType, data?: Record<string, any>): string {
    const messageMap: Record<NotificationType, string> = {
      // Authentication & User Management
      welcome: 'Thank you for joining our family tree community!',
      email_verified: 'Your email address has been verified.',
      approval_granted: 'Your account has been approved. You can now access all features.',
      approval_denied: 'Your account application has been reviewed.',
      role_changed: 'Your user role has been updated.',
      account_suspended: 'Your account has been suspended. Please contact support.',
      account_reactivated: 'Your account has been reactivated. Welcome back!',
      password_reset: 'Your password has been reset successfully.',
      person_link_requested: 'A user has requested to link their account to a person profile.',
      person_link_approved: 'Your profile link request has been approved.',
      person_link_rejected: 'Your profile link request has been denied.',
      person_link_removed: 'Your profile link has been removed.',

      // Content Moderation
      content_submitted: 'Your content has been submitted for review.',
      content_approved: 'Your content has been approved and is now visible.',
      content_rejected: 'Your content submission was not approved.',
      content_flagged: 'Content has been flagged for review.',
      change_approved: 'Your change has been approved.',
      change_rejected: 'Your change was not approved.',
      edit_approved: 'Your edit has been approved.',
      edit_rejected: 'Your edit was not approved.',

      // Social Interactions
      comment_mention: 'Someone mentioned you in a comment.',
      comment_reply: 'Someone replied to your comment.',
      comment_on_content: 'Someone commented on your content.',
      new_comment: 'A new comment has been posted.',
      photo_tagged: 'You have been tagged in a photo.',
      relationship_suggested: 'A relationship has been suggested.',

      // Family Tree Updates
      person_added: 'A new person has been added to the family tree.',
      person_updated: 'A person profile has been updated.',
      relationship_added: 'A new relationship has been added.',
      document_uploaded: 'A new document has been uploaded.',
      photo_uploaded: 'New photos have been uploaded.',
      memory_shared: 'A new memory has been shared.',
      memory_added: 'A new memory has been added.',
      document_shared: 'A document has been shared with you.',

      // Research Collaboration
      research_note_assigned: 'A research task has been assigned to you.',
      research_note_updated: "A research note you're following has been updated.",
      research_note_completed: 'A research task has been completed.',
      research_request: 'A research request has been submitted.',
      research_response: 'Someone responded to your research request.',
      collaboration_invite: 'You have been invited to collaborate on a project.',

      // Reminders & Events
      birthday_reminder: 'Upcoming birthday.',
      anniversary_reminder: 'Upcoming anniversary.',
      death_anniversary: 'Death anniversary.',
      task_reminder: 'You have a task reminder.',
      profile_incomplete: 'Your profile is incomplete. Please update your information.',

      // System & Admin
      system_announcement: 'Important system announcement.',
      system_update: 'System update notification.',
      maintenance_scheduled: 'Scheduled maintenance notification.',
      feature_announcement: 'New feature available.',
      privacy_policy_update: 'Privacy policy has been updated.',
      export_ready: 'Your data export is ready for download.',
      import_completed: 'Your data import has completed successfully.',
      import_failed: 'Your data import has failed.',
      admin_broadcast: 'Important message from administrators.',
      custom: 'Custom notification.',

      // Messaging
      new_message: 'You have a new message.',
      message_mention: 'You were mentioned in a message.',
      message_reply: 'Someone replied to your message.',
      message_reaction: 'Someone reacted to your message.',
      group_chat_added: 'You were added to a group chat.',
      group_chat_removed: 'You were removed from a group chat.',
      group_chat_name_changed: 'The group chat name has been changed.',
      group_chat_member_joined: 'A new member joined the group chat.',
      group_chat_member_left: 'A member left the group chat.',

      // Mentions (additional types)
      note_mention: 'You were mentioned in a note.',
      memory_mention: 'You were mentioned in a memory.',
      feature_request_mention: 'You were mentioned in a feature request.',
    };

    return messageMap[type] || 'You have a new notification.';
  }

  /**
   * Get default priority for notification type
   */
  private getDefaultPriority(type: NotificationType): NotificationPriority {
    const priorityMap: Record<NotificationType, NotificationPriority> = {
      // Critical
      approval_granted: NotificationPriority.CRITICAL,
      approval_denied: NotificationPriority.CRITICAL,
      account_suspended: NotificationPriority.CRITICAL,
      password_reset: NotificationPriority.CRITICAL,

      // High
      account_reactivated: NotificationPriority.HIGH,
      person_link_approved: NotificationPriority.HIGH,
      person_link_removed: NotificationPriority.HIGH,
      comment_mention: NotificationPriority.HIGH,
      research_note_assigned: NotificationPriority.HIGH,
      system_announcement: NotificationPriority.HIGH,
      role_changed: NotificationPriority.HIGH,
      maintenance_scheduled: NotificationPriority.HIGH,
      collaboration_invite: NotificationPriority.HIGH,
      admin_broadcast: NotificationPriority.HIGH,
      privacy_policy_update: NotificationPriority.HIGH,
      import_failed: NotificationPriority.HIGH,
      message_mention: NotificationPriority.HIGH,
      note_mention: NotificationPriority.HIGH,
      memory_mention: NotificationPriority.HIGH,
      feature_request_mention: NotificationPriority.HIGH,

      // Normal
      welcome: NotificationPriority.NORMAL,
      email_verified: NotificationPriority.NORMAL,
      person_link_requested: NotificationPriority.NORMAL,
      person_link_rejected: NotificationPriority.NORMAL,
      content_approved: NotificationPriority.NORMAL,
      content_rejected: NotificationPriority.NORMAL,
      content_submitted: NotificationPriority.NORMAL,
      content_flagged: NotificationPriority.NORMAL,
      change_approved: NotificationPriority.NORMAL,
      change_rejected: NotificationPriority.NORMAL,
      edit_approved: NotificationPriority.NORMAL,
      edit_rejected: NotificationPriority.NORMAL,
      comment_reply: NotificationPriority.NORMAL,
      comment_on_content: NotificationPriority.NORMAL,
      new_comment: NotificationPriority.NORMAL,
      photo_tagged: NotificationPriority.NORMAL,
      relationship_suggested: NotificationPriority.NORMAL,
      research_note_completed: NotificationPriority.NORMAL,
      research_request: NotificationPriority.NORMAL,
      research_response: NotificationPriority.NORMAL,
      feature_announcement: NotificationPriority.NORMAL,
      system_update: NotificationPriority.NORMAL,
      export_ready: NotificationPriority.NORMAL,
      import_completed: NotificationPriority.NORMAL,
      document_shared: NotificationPriority.NORMAL,
      task_reminder: NotificationPriority.NORMAL,
      custom: NotificationPriority.NORMAL,
      new_message: NotificationPriority.NORMAL,
      message_reply: NotificationPriority.NORMAL,
      message_reaction: NotificationPriority.NORMAL,
      group_chat_added: NotificationPriority.NORMAL,

      // Low
      birthday_reminder: NotificationPriority.LOW,
      anniversary_reminder: NotificationPriority.LOW,
      death_anniversary: NotificationPriority.LOW,
      person_added: NotificationPriority.LOW,
      person_updated: NotificationPriority.LOW,
      relationship_added: NotificationPriority.LOW,
      document_uploaded: NotificationPriority.LOW,
      photo_uploaded: NotificationPriority.LOW,
      memory_shared: NotificationPriority.LOW,
      memory_added: NotificationPriority.LOW,
      research_note_updated: NotificationPriority.LOW,
      profile_incomplete: NotificationPriority.LOW,
      group_chat_removed: NotificationPriority.LOW,
      group_chat_name_changed: NotificationPriority.LOW,
      group_chat_member_joined: NotificationPriority.LOW,
      group_chat_member_left: NotificationPriority.LOW,
    };

    return priorityMap[type] || NotificationPriority.NORMAL;
  }

  /**
   * Create a mock notification (when notifications are disabled)
   */
  /**
   * Remove undefined fields from an object
   * Firestore doesn't accept undefined values
   * Recursively cleans nested objects and arrays
   */
  private removeUndefinedFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj
        .filter(item => item !== undefined)
        .map(item => this.removeUndefinedFields(item));
    }

    // Handle objects
    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const result: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          result[key] = this.removeUndefinedFields(obj[key]);
        }
      }
      return result;
    }

    // Return primitive values as-is
    return obj;
  }

  private createMockNotification(params: CreateNotificationParams): UserNotification {
    return {
      id: 'mock-' + Date.now(),
      userId: params.userId,
      type: params.type,
      title: params.title || this.getDefaultTitle(params.type),
      message: params.message || this.getDefaultMessage(params.type, params.data),
      priority: params.priority || this.getDefaultPriority(params.type),
      actionUrl: params.actionUrl,
      actionLabel: params.actionLabel || 'View',
      dismissible: params.dismissible !== false,
      data: params.data || {},
      groupKey: params.groupKey,
      read: false,
      archived: false,
      createdBy: 'system',
      source: 'automated',
      createdAt: new Date(),
    };
  }
}

// Export singleton instance
export const NotificationService = new NotificationServiceImpl();
